import AsyncStorage from "@react-native-async-storage/async-storage";

const QUEUE_KEY = "ferina_offline_queue";

export type OfflineQueueItemStatus = "pending" | "failed";

export type OfflineQueueItem = {
  id: string;
  type: "animal.insert";
  payload: Record<string, unknown>;
  createdAt: string;
  status: OfflineQueueItemStatus;
  error?: string;
};

async function getStoredQueue(): Promise<OfflineQueueItem[]> {
  try {
    const raw = await AsyncStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as OfflineQueueItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function setStoredQueue(items: OfflineQueueItem[]): Promise<void> {
  await AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(items));
}

export const offlineQueue = {
  async getAll(): Promise<OfflineQueueItem[]> {
    return getStoredQueue();
  },

  async getPending(): Promise<OfflineQueueItem[]> {
    const items = await getStoredQueue();
    return items.filter((i) => i.status === "pending");
  },

  async add(item: Omit<OfflineQueueItem, "createdAt" | "status">): Promise<void> {
    const list = await getStoredQueue();
    const newItem: OfflineQueueItem = {
      ...item,
      createdAt: new Date().toISOString(),
      status: "pending",
    };
    list.push(newItem);
    await setStoredQueue(list);
  },

  async remove(id: string): Promise<void> {
    const list = await getStoredQueue();
    await setStoredQueue(list.filter((i) => i.id !== id));
  },

  async markFailed(id: string, error: string): Promise<void> {
    const list = await getStoredQueue();
    const idx = list.findIndex((i) => i.id === id);
    if (idx >= 0) {
      list[idx] = { ...list[idx], status: "failed", error };
      await setStoredQueue(list);
    }
  },

  async clear(): Promise<void> {
    await AsyncStorage.removeItem(QUEUE_KEY);
  },
};

export function createTempId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `temp_${crypto.randomUUID()}`;
  }
  return `temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function isTempId(id: string): boolean {
  return id.startsWith("temp_");
}
